import { gql } from '@apollo/client'

const CONTACT_ATTRIBUTES = gql`
  fragment contactAttributes on Contact {
    id
    name
    email
    number
    isPrimaryContact
    projects {
      id
    }
  }
`

const SUBCONTRACTOR_ATTRIBUTES = gql`
  fragment subcontractorAttributes on Subcontractor {
    id
    company {
      name
      abn
    }
    contacts {
      id
      name
      email
      number
      isPrimaryContact
      projects {
        id
      }
    }
    isBuilder
    createdAt
    updatedAt
    archivedAt
    procoreId
    workers {
      id
      user {
        firstName
        lastName
      }
    }
    projects {
      id
      name
      status
    }
    address
    prequalifications {
      id
      status
      createdAt
      updatedAt
      reviewComments
    }
    integrationConnections {
      id
      integrationIdentifier
      integration {
        integrationType
      }
      sourceId
    }
  }
`

const SUBCONTRACTOR_MINIMUM_ATTRIBUTES = gql`
  fragment subcontractorMinimumAttributes on Subcontractor {
    id
    createdAt
    isBuilder

    company {
      id
      name
      abn
    }
  }
`

const SUBCONTRACTOR_FILES = gql`
  fragment subcontractorFiles on Subcontractor {
    swmsDocs {
      id
      name
      createdAt
      archivedAt
      status
      project {
        id
        name
      }
    }
    insuranceDocs {
      id
      name
      createdAt
      status
      expiry
    }
    msdsDocs {
      id
      name
      createdAt
      status
    }
    documents {
      id
      name
      createdAt
      status
      project {
        id
        name
      }
      type
    }
  }
`

export const GET_SUBCONTRACTOR = gql`
  query subcontractor_UI($subcontractorId: String!) {
    subcontractor(id: $subcontractorId) {
      ...subcontractorAttributes
      tags {
        id
        slug
        name
      }
    }
  }
  ${SUBCONTRACTOR_ATTRIBUTES}
`

export const GET_SUBCONTRACTOR_WITH_FILES = gql`
  query subcontractorWithFiles_UI($subcontractorId: String!) {
    subcontractor(id: $subcontractorId) {
      ...subcontractorAttributes
      ...subcontractorFiles
    }
  }
  ${SUBCONTRACTOR_ATTRIBUTES}
  ${SUBCONTRACTOR_FILES}
`

export const GET_SUBCONTRACTOR_WITH_FILES_LIST = gql`
  query subcontractorFilesList_UI($subcontractorId: String!) {
    subcontractor(id: $subcontractorId) {
      ...subcontractorFiles
    }
  }
  ${SUBCONTRACTOR_FILES}
`

export const GET_SUBCONTRACTORS_BY_PROJECT = gql`
  query subcontractorsByProject_UI(
    $projectId: String!
    $filter: SubcontractorFilterInput
    $pagination: PaginationInput
    $sort: SubcontractorSortInput
    $searchString: String
  ) {
    subcontractorsByProject(
      projectId: $projectId
      filter: $filter
      pagination: $pagination
      sort: $sort
      searchString: $searchString
    ) {
      ...subcontractorAttributes
      lastFileUploadEmail(projectId: $projectId) {
        id
        createdAt
      }
      lastInductionEmail(projectId: $projectId) {
        id
        createdAt
      }
      lastWelcomeEmail(projectId: $projectId) {
        id
        createdAt
      }
      lastFileUploadInvite(projectId: $projectId) {
        id
        createdAt
      }
    }
    subcontractorsByProjectAggregation(
      projectId: $projectId
      filter: $filter
      pagination: $pagination
      sort: $sort
      searchString: $searchString
    ) {
      totalCount
      totalPages
      offset
      limit
    }
  }
  ${SUBCONTRACTOR_ATTRIBUTES}
`

export const GET_SUBCONTRACTORS_BY_BUILDER_FILTER_LIST = gql`
  query subcontractorsByBuilderFilterList_UI(
    $filter: SubcontractorFilterInput
    $pagination: PaginationInput
    $sort: SubcontractorSortInput
    $searchString: String
  ) {
    subcontractorsByBuilder(
      filter: $filter
      pagination: $pagination
      sort: $sort
      searchString: $searchString
    ) {
      ...subcontractorMinimumAttributes
    }
  }
  ${SUBCONTRACTOR_MINIMUM_ATTRIBUTES}
`

export const GET_SUBCONTRACTORS_BY_BUILDER_SEARCH = gql`
  query subcontractorsByBuilderSearch_UI(
    $filter: SubcontractorFilterInput
    $pagination: PaginationInput
    $sort: SubcontractorSortInput
    $searchString: String
  ) {
    subcontractorsByBuilder(
      filter: $filter
      pagination: $pagination
      sort: $sort
      searchString: $searchString
    ) {
      ...subcontractorMinimumAttributes

      addressParts {
        state
      }

      projects {
        id
      }
    }
  }
  ${SUBCONTRACTOR_MINIMUM_ATTRIBUTES}
`

export const GET_SUBCONTRACTORS_BY_PROJECT_FILTER_LIST = gql`
  query subcontractorsByProjectFilterList_UI(
    $projectId: String!
    $filter: SubcontractorFilterInput
    $pagination: PaginationInput
    $sort: SubcontractorSortInput
    $searchString: String
  ) {
    subcontractorsByProject(
      projectId: $projectId
      filter: $filter
      pagination: $pagination
      sort: $sort
      searchString: $searchString
    ) {
      ...subcontractorMinimumAttributes
    }
  }
  ${SUBCONTRACTOR_MINIMUM_ATTRIBUTES}
`

export const GET_PROJECT_SUBBIES_ASSIGNEES = gql`
  query projectSubbieAssignees_UI(
    $projectId: String!
    $filter: SubcontractorFilterInput
    $pagination: PaginationInput
    $sort: SubcontractorSortInput
    $searchString: String
  ) {
    subcontractorsByProject(
      projectId: $projectId
      filter: $filter
      pagination: $pagination
      sort: $sort
      searchString: $searchString
    ) {
      id
      createdAt
      isBuilder

      company {
        id
        name
        abn
      }
      contacts {
        id
        email
        name
        number
        isPrimaryContact
        subcontractorId
        projects {
          id
        }
      }
    }
    subcontractorsByProjectAggregation(
      projectId: $projectId
      filter: $filter
      pagination: $pagination
      sort: $sort
      searchString: $searchString
    ) {
      totalCount
      totalPages
      offset
      limit
    }
  }
`

export const GET_PROJECT_SUBBIES_FOR_SEARCH = gql`
  query projectSubbiesForSearch_UI(
    $projectId: String!
    $filter: SubcontractorFilterInput
    $pagination: PaginationInput
    $sort: SubcontractorSortInput
    $searchString: String
  ) {
    subcontractorsByProject(
      projectId: $projectId
      filter: $filter
      pagination: $pagination
      sort: $sort
      searchString: $searchString
    ) {
      id
      createdAt
      isBuilder

      company {
        id
        name
        abn
      }
    }
    subcontractorsByProjectAggregation(
      projectId: $projectId
      filter: $filter
      pagination: $pagination
      sort: $sort
      searchString: $searchString
    ) {
      totalCount
      totalPages
      offset
      limit
    }
  }
`

const SUBCONTRACTOR_ATTRIBUTES_FOR_INTEGRATION = gql`
  fragment subcontractorAttributesForIntegration on Subcontractor {
    id
    company {
      name
      abn
    }
    isBuilder
    createdAt
    updatedAt
    integrationConnections {
      id
      integrationIdentifier
      integration {
        integrationType
      }
      sourceId
    }
  }
`

export const GET_BUILDER_SUBBIES_FOR_AGAVE = gql`
  query agaveBuilderSubbies_UI(
    $filter: SubcontractorFilterInput
    $pagination: PaginationInput
    $sort: SubcontractorSortInput
    $searchString: String
  ) {
    subcontractorsByBuilder(
      filter: $filter
      pagination: $pagination
      sort: $sort
      searchString: $searchString
    ) {
      ...subcontractorAttributesForIntegration
      procoreId
    }
    subcontractorsByBuilderAggregation(
      filter: $filter
      pagination: $pagination
      sort: $sort
      searchString: $searchString
    ) {
      totalCount
      totalPages
      offset
      limit
    }
  }
  ${SUBCONTRACTOR_ATTRIBUTES_FOR_INTEGRATION}
`

export const GET_BUILDER_SUBBIES_FOR_BUILDXACT = gql`
  query buildxactBuilderSubbies_UI(
    $filter: SubcontractorFilterInput
    $pagination: PaginationInput
    $sort: SubcontractorSortInput
    $searchString: String
  ) {
    subcontractorsByBuilder(
      filter: $filter
      pagination: $pagination
      sort: $sort
      searchString: $searchString
    ) {
      ...subcontractorAttributesForIntegration
    }
    subcontractorsByBuilderAggregation(
      filter: $filter
      pagination: $pagination
      sort: $sort
      searchString: $searchString
    ) {
      totalCount
      totalPages
      offset
      limit
    }
  }
  ${SUBCONTRACTOR_ATTRIBUTES_FOR_INTEGRATION}
`

export const GET_BUILDER_SUBBIE_ASSIGNEES = gql`
  query builderSubbieAssignees_UI(
    $filter: SubcontractorFilterInput
    $pagination: PaginationInput
    $sort: SubcontractorSortInput
    $searchString: String
  ) {
    subcontractorsByBuilder(
      filter: $filter
      pagination: $pagination
      sort: $sort
      searchString: $searchString
    ) {
      id
      company {
        name
        abn
      }
      contacts {
        id
        name
        email
        number
        isPrimaryContact
        projects {
          id
        }
      }
    }
    subcontractorsByBuilderAggregation(
      filter: $filter
      pagination: $pagination
      sort: $sort
      searchString: $searchString
    ) {
      totalCount
      totalPages
      offset
      limit
    }
  }
`

export const GET_BUILDER_SUBBIES_FOR_SEARCH = gql`
  query builderSubbiesForSearch_UI(
    $filter: SubcontractorFilterInput
    $pagination: PaginationInput
    $sort: SubcontractorSortInput
    $searchString: String
  ) {
    subcontractorsByBuilder(
      filter: $filter
      pagination: $pagination
      sort: $sort
      searchString: $searchString
    ) {
      id
      isBuilder
      procoreId
      company {
        name
        abn
      }
    }
    subcontractorsByBuilderAggregation(
      filter: $filter
      pagination: $pagination
      sort: $sort
      searchString: $searchString
    ) {
      totalCount
      totalPages
      offset
      limit
    }
  }
`

export const CREATE_SUBCONTRACTOR = gql`
  mutation createSubcontractor_UI(
    $companyName: String!
    $companyEmail: String!
    $companyNumber: String
    $companyAbn: String
    $companyContactName: String
    $projectIds: [String]
    $builderId: String!
    $address: String
  ) {
    createSubcontractor(
      input: {
        company: {
          name: $companyName
          email: $companyEmail
          number: $companyNumber
          abn: $companyAbn
          contactName: $companyContactName
        }
        projectIds: $projectIds
        builderId: $builderId
        address: $address
      }
    ) {
      ...subcontractorAttributes
    }
  }
  ${SUBCONTRACTOR_ATTRIBUTES}
`

export const CREATE_SUBCONTRACTORS = gql`
  mutation createSubcontractors_UI($input: [CreateSubcontractorInput!]) {
    createSubcontractors(input: $input) {
      count
    }
  }
`

export const UPDATE_SUBCONTRACTOR = gql`
  mutation updateSubcontractor_UI(
    $id: String!
    $companyName: String!
    $companyEmail: String!
    $companyNumber: String
    $companyAbn: String
    $companyContactName: String
    $projectIds: [String]
    $builderId: String
    $address: String
  ) {
    updateSubcontractor(
      id: $id
      input: {
        company: {
          name: $companyName
          email: $companyEmail
          number: $companyNumber
          abn: $companyAbn
          contactName: $companyContactName
        }
        projectIds: $projectIds
        builderId: $builderId
        address: $address
      }
    ) {
      ...subcontractorAttributes
    }
  }
  ${SUBCONTRACTOR_ATTRIBUTES}
`

export const UPDATE_SUBCONTRACTOR_DETAILS = gql`
  mutation updateSubcontractorDetails_UI(
    $id: String!
    $companyName: String!
    $companyEmail: String!
    $companyNumber: String
    $companyAbn: String
    $companyContactName: String
    $address: String
  ) {
    updateSubcontractorDetails(
      id: $id
      input: {
        company: {
          name: $companyName
          email: $companyEmail
          number: $companyNumber
          abn: $companyAbn
          contactName: $companyContactName
        }
        address: $address
      }
    ) {
      ...subcontractorAttributes
    }
  }
  ${SUBCONTRACTOR_ATTRIBUTES}
`

export const ASSIGN_SUBCONTRACTORS_TO_PROJECT = gql`
  mutation assignSubcontractorsToProject_UI(
    $id: String!
    $subcontractorIds: [String!]!
  ) {
    assignSubcontractorsToProject(
      id: $id
      subcontractorIds: $subcontractorIds
    ) {
      id
    }
  }
`

export const UNASSIGN_SUBCONTRACTORS_FROM_PROJECT = gql`
  mutation unassignSubcontractorsFromProject_UI(
    $id: String!
    $subcontractorIds: [String!]!
  ) {
    unassignSubcontractorsFromProject(
      id: $id
      subcontractorIds: $subcontractorIds
    ) {
      id
    }
  }
`
export const ARCHIVE_SUBCONTRACTOR = gql`
  mutation archiveSubcontractor_UI($id: String!) {
    archiveSubcontractor(id: $id) {
      id
    }
  }
`

export const GET_SUBCONTRACTOR_ARCHIVE_INFO = gql`
  query getSubcontractorArchiveInfo_UI($id: String!) {
    getSubcontractorArchiveInfo(id: $id) {
      workersCount
      swmsDocsCount
      insuranceDocsCount
      sdsDocsCount
      documentsCount
      prequalificationsCount
      timesheetsCount
      integrationConnectionsCount
    }
  }
`

export const GET_BULK_SUBCONTRACTOR_ARCHIVE_INFO = gql`
  query getBulkSubcontractorArchiveInfo_UI(
    $filter: SubcontractorFilterInput
    $pagination: PaginationInput
    $sort: SubcontractorSortInput
  ) {
    subcontractorsByBuilder(
      filter: $filter
      pagination: $pagination
      sort: $sort
    ) {
      id
      company {
        name
        abn
      }
      stats {
        workersCount
        swmsDocsCount
        insuranceDocsCount
        msdsDocsCount
        documentsCount
        prequalificationsCount
        timesheetsCount
        integrationConnectionsCount
      }
    }
  }
`

export const BULK_ARCHIVE_SUBCONTRACTORS = gql`
  mutation bulkArchiveSubcontractors_UI($ids: [String!]!) {
    bulkArchiveSubcontractors(ids: $ids)
  }
`

export const CONNECT_PROCORE_SUBCONTRACTOR = gql`
  mutation connectProcoreSubcontractor_UI(
    $id: String!
    $input: ProcoreSubcontractorInput
  ) {
    connectProcoreSubcontractor(id: $id, input: $input) {
      id
      procoreId
    }
  }
`

export const CONNECT_PROCORE_SUBCONTRACTORS = gql`
  mutation connectProcoreSubcontractors_UI(
    $input: [ProcoreSubcontractorConnectInput!]!
  ) {
    connectProcoreSubcontractors(input: $input) {
      id
      procoreId
    }
  }
`

export const DISCONNECT_PROCORE_SUBCONTRACTOR = gql`
  mutation disconnectProcoreSubcontractor_UI($id: String!) {
    disconnectProcoreSubcontractor(id: $id) {
      id
      procoreId
    }
  }
`

export const GET_CONTACT = gql`
  query contact_UI($contactId: String!) {
    contact(id: $contactId) {
      ...contactAttributes
    }
  }
  ${CONTACT_ATTRIBUTES}
`

export const CREATE_CONTACT_FOR_SUBCONTRACTOR = gql`
  mutation createContactForSubcontractor_UI(
    $subcontractorId: String!
    $input: ContactInput!
  ) {
    createContact(subcontractorId: $subcontractorId, input: $input) {
      ...contactAttributes
    }
  }
  ${CONTACT_ATTRIBUTES}
`

export const UPDATE_CONTACT = gql`
  mutation updateContact_UI($contactId: String!, $input: ContactInput!) {
    updateContact(contactId: $contactId, input: $input) {
      ...contactAttributes
    }
  }
  ${CONTACT_ATTRIBUTES}
`

export const DELETE_CONTACT = gql`
  mutation deleteContact_UI($contactId: String!) {
    deleteContact(contactId: $contactId) {
      ...contactAttributes
    }
  }
  ${CONTACT_ATTRIBUTES}
`

export const TOGGLE_PRIMARY_CONTACT = gql`
  mutation togglePrimaryContact_UI($contactId: String!) {
    togglePrimaryContact(contactId: $contactId) {
      ...contactAttributes
    }
  }
  ${CONTACT_ATTRIBUTES}
`

export const ASSIGN_CONTACT_TO_PROJECT = gql`
  mutation assignContactToProject_UI($contactId: String!, $projectId: String!) {
    assignContactToProject(contactId: $contactId, projectId: $projectId) {
      ...contactAttributes
    }
  }
  ${CONTACT_ATTRIBUTES}
`
export const REMOVE_CONTACT_FROM_PROJECT = gql`
  mutation removeContactFromProject_UI(
    $contactId: String!
    $projectId: String!
  ) {
    removeContactFromProject(contactId: $contactId, projectId: $projectId) {
      ...contactAttributes
    }
  }
  ${CONTACT_ATTRIBUTES}
`

export const GET_SUBBIE_INFO = gql`
  query subbieInfo_UI($id: String!) {
    subcontractor(id: $id) {
      id
      company {
        name
        abn
      }
      contacts {
        id
        name
        email
        number
        isPrimaryContact

        projects {
          id
        }
      }
    }
  }
`

export const GET_CONTACT_INFO = gql`
  query contactInfo_UI($id: String!) {
    contact(id: $id) {
      id
      name
      email
      number

      subcontractor {
        id
        company {
          name
        }
      }
    }
  }
`

export const MERGE_SUBCONTRACTORS = gql`
  mutation saMergeSubcontractors_UI($keepId: String!, $archiveId: String!) {
    saMergeSubcontractors(keepId: $keepId, archiveId: $archiveId) {
      id
    }
  }
`

export const PUBLIC_SUBCONTRACTORS = gql`
  query publicSubcontractors_UI(
    $builderId: String!
    $filter: SubcontractorFilterInput_Public
    $pagination: PaginationInput
  ) {
    subcontractors_public(
      builderId: $builderId
      filter: $filter
      pagination: $pagination
    ) {
      id
      isBuilder
      company {
        name
        abn
      }
    }
  }
`
